import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { useCourse } from '../../contexts/CourseContext';
import {
  EditNoteOutlined,
  QuizOutlined,
  MenuBookOutlined,
  FactCheckOutlined,
  DeleteOutlineOutlined,
  AddOutlined
} from '../../icons';
import DashboardTopbar from '../../components/DashboardTopbar';
import Dropdown from '../../components/Dropdown';
import PlansModal from '../../components/PlansModal';
import { getSubjectColor } from '../../utils/subjectColors';
import { STUDY_DIARY_LIMIT } from '../../planLimits';
import './StudyLog.css';

const apiUrl = process.env.REACT_APP_API_URL;

const ACTIVITY_TYPES = [
  { key: 'questoes', label: 'Questões', icon: QuizOutlined },
  { key: 'material', label: 'Material', icon: MenuBookOutlined },
  { key: 'revisao', label: 'Revisão', icon: FactCheckOutlined }
];

const ACTIVITY_LABELS = { questoes: 'Questões', material: 'Material', revisao: 'Revisão' };
const ADD_CUSTOM_TOPIC_VALUE = '__add_custom_topic__';

function todayIso() {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  return new Date(now.getTime() - offset * 60000).toISOString().slice(0, 10);
}

function emptyForm() {
  return {
    topicId: '',
    topicProgressPercent: '',
    activityType: 'questoes',
    durationMinutes: '',
    studiedAt: todayIso(),
    questionsCount: '',
    questionsCorrect: '',
    questionsReference: '',
    materialName: '',
    materialUrl: '',
    notes: ''
  };
}

function StudyLog() {
  const location = useLocation();
  const {
    subjectId: incomingSubjectId,
    topicId: incomingTopicId,
    subjectName: incomingSubjectName,
    topicName: incomingTopicName,
    durationMinutes: incomingDurationMinutes
  } = location.state || {};
  const { activeCourseId, courses, loadingCourses } = useCourse();
  const activeCourseName = courses.find((c) => String(c.id) === String(activeCourseId))?.name;

  const [subjects, setSubjects] = useState([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState('');

  const [topics, setTopics] = useState([]);
  const [showAddTopicForm, setShowAddTopicForm] = useState(false);
  const [newTopicName, setNewTopicName] = useState('');
  const [addingTopic, setAddingTopic] = useState(false);
  const [addTopicError, setAddTopicError] = useState('');

  const [form, setForm] = useState(() => ({
    ...emptyForm(),
    durationMinutes: incomingDurationMinutes ? String(incomingDurationMinutes) : ''
  }));
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [hasFreePlan, setHasFreePlan] = useState(false);
  const [showDiaryPaywall, setShowDiaryPaywall] = useState(false);
  const [historyFilter, setHistoryFilter] = useState(() => (
    incomingSubjectId
      ? { subjectId: incomingSubjectId, topicId: incomingTopicId || null, subjectName: incomingSubjectName, topicName: incomingTopicName }
      : null
  ));

  const fetchSessions = useCallback(async (courseId, filter = {}) => {
    setLoadingSessions(true);
    try {
      const params = {
        ...(courseId ? { courseId } : {}),
        ...(filter.subjectId ? { subjectId: filter.subjectId } : {}),
        ...(filter.topicId ? { topicId: filter.topicId } : {})
      };
      const response = await axios.get(apiUrl + 'studySessions', { params });
      setSessions(response.data.sessions || []);
      setHasFreePlan(!!response.data.hasFreePlan);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingSessions(false);
    }
  }, []);

  useEffect(() => {
    if (incomingSubjectId) {
      setHistoryFilter({ subjectId: incomingSubjectId, topicId: incomingTopicId || null, subjectName: incomingSubjectName, topicName: incomingTopicName });
    }
  }, [incomingSubjectId, incomingTopicId, incomingSubjectName, incomingTopicName]);

  useEffect(() => {
    if (!activeCourseId) {
      setSubjects([]);
      setSelectedSubjectId('');
      setSessions([]);
      return;
    }

    const loadSubjects = async () => {
      try {
        const response = await axios.get(apiUrl + `courseSubjects/${activeCourseId}`, {
          params: { scope: 'plan' }
        });
        const loadedSubjects = response.data.subjects || [];
        setSubjects(loadedSubjects);
        if (incomingSubjectId && loadedSubjects.some((s) => String(s.id) === String(incomingSubjectId))) {
          setSelectedSubjectId(String(incomingSubjectId));
        }
      } catch (error) {
        console.error(error);
        alert('Erro ao carregar as matérias desse concurso.');
      }
    };

    setSelectedSubjectId('');
    loadSubjects();
  }, [activeCourseId, incomingSubjectId]);

  useEffect(() => {
    if (!activeCourseId) return;
    fetchSessions(activeCourseId, historyFilter || {});
  }, [activeCourseId, historyFilter, fetchSessions]);

  useEffect(() => {
    setShowAddTopicForm(false);
    setNewTopicName('');
    setAddTopicError('');

    if (!activeCourseId || !selectedSubjectId) {
      setTopics([]);
      return;
    }

    const loadTopics = async () => {
      try {
        const response = await axios.get(apiUrl + 'topics', {
          params: { courseId: activeCourseId, subjectId: selectedSubjectId, scope: 'plan' }
        });
        setTopics(response.data.topics || []);
      } catch (error) {
        console.error(error);
      }
    };

    loadTopics();
  }, [activeCourseId, selectedSubjectId]);

  const handleAddCustomTopic = async (event) => {
    event.preventDefault();
    if (!newTopicName.trim()) return;
    const courseSubjectId = subjects.find((s) => String(s.id) === String(selectedSubjectId))?.courseSubjectId;
    if (!courseSubjectId) return;

    setAddingTopic(true);
    setAddTopicError('');
    try {
      const response = await axios.post(apiUrl + 'myTopics', {
        course_subject_id: courseSubjectId,
        name: newTopicName.trim()
      });
      const newTopic = { id: response.data.id, name: newTopicName.trim(), isCustom: true };
      setTopics((prev) => [...prev, newTopic].sort((a, b) => a.name.localeCompare(b.name)));
      updateField('topicId', String(newTopic.id));
      setNewTopicName('');
      setShowAddTopicForm(false);
    } catch (error) {
      setAddTopicError(error.response?.data?.message || 'Erro ao adicionar assunto.');
    } finally {
      setAddingTopic(false);
    }
  };

  const handleDeleteCustomTopic = async (topicId) => {
    if (!window.confirm('Excluir esse assunto personalizado?')) return;
    try {
      await axios.delete(apiUrl + `myTopics/${topicId}`);
      setTopics((prev) => prev.filter((topic) => topic.id !== topicId));
      if (String(form.topicId) === String(topicId)) {
        updateField('topicId', '');
      }
    } catch (error) {
      console.error(error);
      alert('Erro ao excluir o assunto.');
    }
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');

    if (!activeCourseId || !selectedSubjectId) {
      setErrorMessage('Selecione a matéria.');
      return;
    }
    if (!form.durationMinutes || Number(form.durationMinutes) <= 0) {
      setErrorMessage('Informe quanto tempo você estudou.');
      return;
    }
    if (form.activityType === 'material' && !form.materialName && !form.materialUrl) {
      setErrorMessage('Informe o nome ou o link do material.');
      return;
    }
    if (form.activityType === 'questoes') {
      if (form.questionsCount === '' || form.questionsCount === null || Number(form.questionsCount) <= 0) {
        setErrorMessage('Informe quantas questões você fez.');
        return;
      }
      if (form.questionsCorrect === '' || form.questionsCorrect === null || Number(form.questionsCorrect) < 0) {
        setErrorMessage('Informe quantos acertos você teve.');
        return;
      }
      if (Number(form.questionsCorrect) > Number(form.questionsCount)) {
        setErrorMessage('Os acertos não podem ser maiores que as questões feitas.');
        return;
      }
    }
    if (hasFreePlan && sessions.length >= STUDY_DIARY_LIMIT) {
      setShowDiaryPaywall(true);
      return;
    }

    setSaving(true);
    try {
      await axios.post(apiUrl + 'studySessions', {
        courseId: Number(activeCourseId),
        subjectId: Number(selectedSubjectId),
        topicId: form.topicId ? Number(form.topicId) : null,
        topicProgressPercent: form.topicId && form.topicProgressPercent !== '' ? Number(form.topicProgressPercent) : null,
        activityType: form.activityType,
        durationMinutes: Number(form.durationMinutes),
        studiedAt: form.studiedAt,
        questionsCount: form.questionsCount || null,
        questionsCorrect: form.questionsCorrect || null,
        questionsReference: form.questionsReference || null,
        materialName: form.materialName || null,
        materialUrl: form.materialUrl || null,
        notes: form.notes || null
      });

      setForm(emptyForm());
      fetchSessions(activeCourseId, historyFilter || {});
    } catch (error) {
      if (error.response?.status === 403 && error.response.data?.code === 'PLAN_LIMIT_REACHED') {
        setShowDiaryPaywall(true);
      } else {
        console.error(error);
        setErrorMessage(error.response?.data?.message || 'Erro ao registrar a sessão de estudo.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (sessionId) => {
    if (!window.confirm('Excluir esse registro de estudo?')) return;
    try {
      await axios.delete(apiUrl + `studySessions/${sessionId}`);
      setSessions((prev) => prev.filter((session) => session.id !== sessionId));
    } catch (error) {
      console.error(error);
      alert('Erro ao excluir o registro.');
    }
  };

  if (loadingCourses) {
    return (
      <div className="App dashboard-page study-log-page">
        <DashboardTopbar label="Diário de Estudos" />
        <div className="loading-message">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="App dashboard-page study-log-page">
      <DashboardTopbar label="Diário de Estudos" />

      {showDiaryPaywall && (
        <PlansModal
          trackingPrefix="trial_wall_diario_estudos"
          heroTitle="Limite do diário de estudos alcançado"
          heroSubtitle={`No plano grátis você pode registrar até ${STUDY_DIARY_LIMIT} sessões de estudo. Libere acesso completo para registrar sem limites.`}
          onClose={() => setShowDiaryPaywall(false)}
        />
      )}

      <h2 className="study-log-title">
        <EditNoteOutlined className="study-log-title-icon" />
        Diário de estudos
      </h2>
      <p className="study-log-subtitle">
        Registre suas sessões de estudo para acompanhar sua evolução ao longo do tempo.
      </p>

      {courses.length === 0 ? (
        <div className="empty-message">
          Nenhum concurso disponível para registrar estudos ainda.
        </div>
      ) : !activeCourseId ? (
        <div className="empty-message">
          Selecione um concurso no topo da tela para começar a registrar seus estudos.
        </div>
      ) : (
        <>
          {activeCourseName && (
            <div className="study-log-active-course">Concurso: <strong>{activeCourseName}</strong></div>
          )}

          <form className="study-log-form" onSubmit={handleSubmit}>
            <div className="study-log-field">
              <label>Matéria</label>
              <div className="study-log-subject-list" role="listbox" aria-label="Matéria">
                {subjects.map((subject) => (
                  <button
                    type="button"
                    key={subject.id}
                    role="option"
                    aria-selected={String(selectedSubjectId) === String(subject.id)}
                    className={`study-log-subject-option ${String(selectedSubjectId) === String(subject.id) ? 'active' : ''}`}
                    onClick={() => setSelectedSubjectId(String(subject.id))}
                  >
                    {subject.name}
                  </button>
                ))}
              </div>
            </div>

            {selectedSubjectId && (
              <div className="study-log-field">
                <label>Assunto (opcional)</label>
                <Dropdown
                  value={form.topicId}
                  onChange={(value) => {
                    if (value === ADD_CUSTOM_TOPIC_VALUE) {
                      setShowAddTopicForm(true);
                      return;
                    }
                    setForm((prev) => ({ ...prev, topicId: value, topicProgressPercent: '' }));
                  }}
                  options={[
                    { value: '', label: 'Sem assunto específico' },
                    ...topics.map((topic) => ({
                      value: String(topic.id),
                      label: topic.isCustom ? `${topic.name} (personalizado)` : topic.name
                    })),
                    { value: ADD_CUSTOM_TOPIC_VALUE, label: 'Adicionar assunto personalizado', icon: <AddOutlined style={{ fontSize: 16 }} /> }
                  ]}
                  headerLabel="Selecione um assunto"
                  placeholder="Sem assunto específico"
                  ariaLabel="Assunto"
                  className="study-log-dropdown dropdown--stretch"
                />

                {showAddTopicForm && (
                  <div className="study-log-add-topic">
                    <input
                      type="text"
                      value={newTopicName}
                      onChange={(e) => setNewTopicName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddCustomTopic(e);
                        }
                      }}
                      placeholder="Nome do novo assunto"
                      maxLength={255}
                      autoFocus
                    />
                    <button type="button" onClick={handleAddCustomTopic} disabled={addingTopic || !newTopicName.trim()}>
                      {addingTopic ? 'Adicionando...' : 'Adicionar'}
                    </button>
                    <button
                      type="button"
                      className="study-log-add-topic-cancel"
                      onClick={() => {
                        setShowAddTopicForm(false);
                        setNewTopicName('');
                        setAddTopicError('');
                      }}
                    >
                      Cancelar
                    </button>
                    {addTopicError && <span className="study-log-add-topic-error">{addTopicError}</span>}
                  </div>
                )}

                {topics.some((topic) => topic.isCustom) && (
                  <div className="study-log-custom-topics">
                    {topics
                      .filter((topic) => topic.isCustom)
                      .map((topic) => (
                        <span className="study-log-custom-topic-chip" key={topic.id}>
                          {topic.name}
                          <button
                            type="button"
                            onClick={() => handleDeleteCustomTopic(topic.id)}
                            aria-label={`Excluir assunto ${topic.name}`}
                          >
                            <DeleteOutlineOutlined style={{ fontSize: 14 }} />
                          </button>
                        </span>
                      ))}
                  </div>
                )}
              </div>
            )}

            {selectedSubjectId && form.topicId && (
              <div className="study-log-field">
                <label>Quanto você sente que avançou nesse assunto até agora?</label>
                <div className="study-log-progress-presets">
                  {[25, 50, 75, 100].map((value) => (
                    <button
                      type="button"
                      key={value}
                      className={`study-log-progress-btn ${Number(form.topicProgressPercent) === value ? 'active' : ''}`}
                      onClick={() => updateField('topicProgressPercent', String(value))}
                    >
                      {value}%
                    </button>
                  ))}
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={form.topicProgressPercent || 0}
                  onChange={(e) => updateField('topicProgressPercent', e.target.value)}
                  className="study-log-progress-slider"
                />
                <span className="study-log-progress-value">
                  {form.topicProgressPercent !== '' ? `${form.topicProgressPercent}%` : 'Não informado'}
                </span>
              </div>
            )}

            {selectedSubjectId && (
              <>
                <div className="study-log-field">
                  <label>O que você fez?</label>
                  <div className="study-log-activity-types">
                    {ACTIVITY_TYPES.map((type) => (
                      <button
                        type="button"
                        key={type.key}
                        className={`study-log-activity-btn ${form.activityType === type.key ? 'active' : ''}`}
                        onClick={() => updateField('activityType', type.key)}
                      >
                        <type.icon style={{ fontSize: 18 }} />
                        {type.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="study-log-row">
                  <div className="study-log-field">
                    <label>Data</label>
                    <input
                      type="date"
                      value={form.studiedAt}
                      onChange={(e) => updateField('studiedAt', e.target.value)}
                    />
                  </div>
                  <div className="study-log-field">
                    <label>Tempo (minutos)</label>
                    <input
                      type="number"
                      min="1"
                      max="600"
                      value={form.durationMinutes}
                      onChange={(e) => updateField('durationMinutes', e.target.value)}
                    />
                  </div>
                </div>

                {form.activityType === 'questoes' && (
                  <div className="study-log-row">
                    <div className="study-log-field">
                      <label>Questões feitas</label>
                      <input
                        type="number"
                        min="0"
                        value={form.questionsCount}
                        onChange={(e) => updateField('questionsCount', e.target.value)}
                      />
                    </div>
                    <div className="study-log-field">
                      <label>Acertos</label>
                      <input
                        type="number"
                        min="0"
                        value={form.questionsCorrect}
                        onChange={(e) => updateField('questionsCorrect', e.target.value)}
                      />
                    </div>
                    <div className="study-log-field study-log-field-wide">
                      <label>Referência (opcional)</label>
                      <input
                        type="text"
                        placeholder="ex.: simulado X, questões 1-20"
                        value={form.questionsReference}
                        onChange={(e) => updateField('questionsReference', e.target.value)}
                      />
                    </div>
                  </div>
                )}

                {form.activityType === 'material' && (
                  <div className="study-log-row">
                    <div className="study-log-field study-log-field-wide">
                      <label>Nome do material</label>
                      <input
                        type="text"
                        placeholder="ex.: PDF de Direito Administrativo"
                        value={form.materialName}
                        onChange={(e) => updateField('materialName', e.target.value)}
                      />
                    </div>
                    <div className="study-log-field study-log-field-wide">
                      <label>Link (opcional)</label>
                      <input
                        type="url"
                        placeholder="https://..."
                        value={form.materialUrl}
                        onChange={(e) => updateField('materialUrl', e.target.value)}
                      />
                    </div>
                  </div>
                )}

                <div className="study-log-field">
                  <label>Observações (opcional)</label>
                  <textarea
                    rows={2}
                    value={form.notes}
                    onChange={(e) => updateField('notes', e.target.value)}
                  />
                </div>

                {errorMessage && <div className="study-log-error">{errorMessage}</div>}

                <button type="submit" className="study-log-submit" disabled={saving}>
                  {saving ? 'Salvando...' : 'Registrar sessão de estudo'}
                </button>
              </>
            )}
          </form>

          <div className="study-log-history">
            <h3>Histórico</h3>
            {historyFilter && (
              <div className="study-log-filter-banner">
                <span>
                  Mostrando histórico de: <strong>{historyFilter.subjectName || 'matéria selecionada'}</strong>
                  {historyFilter.topicId && (
                    <> · <strong>{historyFilter.topicName || 'assunto selecionado'}</strong></>
                  )}
                </span>
                <button type="button" onClick={() => setHistoryFilter(null)}>
                  Limpar filtro
                </button>
              </div>
            )}
            {loadingSessions ? (
              <div className="loading-message">Carregando histórico...</div>
            ) : sessions.length === 0 ? (
              <div className="empty-message">
                {historyFilter
                  ? 'Nenhuma sessão registrada para esse filtro ainda.'
                  : 'Nenhuma sessão registrada para esse concurso ainda.'}
              </div>
            ) : (
              sessions.map((session) => {
                const activityMeta = ACTIVITY_TYPES.find((type) => type.key === session.activity_type);
                const subjectIndex = subjects.findIndex((s) => String(s.id) === String(session.subject_id));
                const subjectColor = getSubjectColor(subjectIndex >= 0 ? subjectIndex : 0).border;
                const showPercentage =
                  session.activity_type === 'questoes' &&
                  session.questions_count != null &&
                  Number(session.questions_count) > 0 &&
                  session.questions_correct != null;
                const percentage = showPercentage
                  ? Math.round((Number(session.questions_correct) / Number(session.questions_count)) * 100)
                  : null;

                const detailParts = [
                  new Date(`${session.studied_at}`.slice(0, 10) + 'T00:00:00').toLocaleDateString('pt-BR'),
                  `${session.duration_minutes} min`
                ];
                if (session.activity_type === 'questoes' && session.questions_count != null) {
                  detailParts.push(
                    session.questions_correct != null
                      ? `${session.questions_correct} de ${session.questions_count} questões corretas`
                      : `${session.questions_count} questões`
                  );
                }
                if (session.activity_type === 'material' && session.material_name) {
                  detailParts.push(session.material_name);
                }

                return (
                  <div className="study-log-session-card" key={session.id}>
                    <span className="study-log-session-bar" style={{ backgroundColor: subjectColor }} />

                    <div className="study-log-session-body">
                      <div className="study-log-session-header">
                        <span className="study-log-session-subject">
                          {session.subject_name}
                          {session.topic_name && (
                            <span className="study-log-session-topic"> · {session.topic_name}</span>
                          )}
                        </span>
                        <span className={`study-log-badge study-log-badge-${session.activity_type}`}>
                          {activityMeta && <activityMeta.icon style={{ fontSize: 14 }} />}
                          {ACTIVITY_LABELS[session.activity_type]}
                        </span>
                        <div className="study-log-session-header-actions">
                          {showPercentage && (
                            <span className="study-log-session-percentage">{percentage}%</span>
                          )}
                          {session.topic_progress_percent != null && (
                            <span className="study-log-badge study-log-badge-progress">
                              Avanço no assunto: {session.topic_progress_percent}%
                            </span>
                          )}
                          <button
                            type="button"
                            className="study-log-session-delete"
                            onClick={() => handleDelete(session.id)}
                            aria-label="Excluir registro"
                          >
                            <DeleteOutlineOutlined style={{ fontSize: 18 }} />
                          </button>
                        </div>
                      </div>

                      <div className="study-log-session-details">{detailParts.join(' · ')}</div>

                      {session.activity_type === 'questoes' && session.questions_reference && (
                        <div className="study-log-session-line">{session.questions_reference}</div>
                      )}
                      {session.activity_type === 'material' && session.material_url && (
                        <a
                          className="study-log-session-line study-log-session-link"
                          href={session.material_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Abrir material
                        </a>
                      )}
                      {session.notes && <div className="study-log-session-notes">{session.notes}</div>}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default StudyLog;
